import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useGetOrganizerQuery, useUpdateOrganizerMutation } from '../../Api/organizers';
import { useGlobalState } from '../../App/globalState';
import { formatPhoneNumber, fromLocalPhone } from '../../Helpers/phone-formatter';
import useTranslate from '../../Hooks/useTranslate';
import Button from '../../Style-guide/Button';
import Input from '../../Style-guide/Input';
import Title from '../../Style-guide/Title';
import validateForm from '../Profile/validator';
import OrganizerPageWrapper from './style/organizerWrapper';

const checkForChanges = ({ prevData, newData }) => {
  let hasChanges = false;

  Object.keys(newData).forEach((key) => {
    if (key === 'phone') {
      if (newData[key] !== fromLocalPhone(prevData.phone)) {
        hasChanges = true;
      }
    } else if (key === 'newPassword' && newData[key] !== '') {
      hasChanges = true;
    } else if (prevData[key] && prevData[key] !== newData[key]) {
      hasChanges = true;
    }
  });

  return hasChanges;
};

const OrganizerAccount = () => {
  const { globalState } = useGlobalState();
  const organizerId = globalState?.organizer?.id;
  const { mutate: updateOrganizerRequest, isSuccess: isUpdateSuccess } =
    useUpdateOrganizerMutation();
  const queryClient = useQueryClient();
  const tr = useTranslate();

  const [organizerData, setOrganizerData] = useState({
    phone: '',
    currentPassword: '',
    newPassword: '',
  });

  const [errors, setErrors] = useState({
    phone: '',
    currentPassword: '',
    newPassword: '',
  });

  const { data, isLoading: isGetOrganizerLoading } = useGetOrganizerQuery({
    params: { id: organizerId },
    enabled: !!organizerId,
    retry: 0,
  });

  useEffect(() => {
    if (organizerId && data && !isGetOrganizerLoading) {
      setOrganizerData((prev) => ({
        ...prev,
        phone: formatPhoneNumber(fromLocalPhone(data.phone)) || '',
      }));
    }
  }, [organizerId, data, isGetOrganizerLoading]);

  useEffect(() => {
    if (isUpdateSuccess) {
      queryClient.invalidateQueries({
        queryKey: ['organizers'],
      });
    }
  }, [isUpdateSuccess, queryClient]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const formattedValue = name === 'phone' ? formatPhoneNumber(value) : value;

    setOrganizerData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateForm(organizerData, tr);
    setErrors(validationErrors);

    const isFormValid = Object.values(validationErrors).every((msg) => msg === '');
    const hasChanges = checkForChanges({ prevData: data, newData: organizerData });

    if (hasChanges && isFormValid) {
      updateOrganizerRequest({
        data: organizerData,
        params: { id: organizerId },
      });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSubmit(event);
    }
  };

  return (
    <OrganizerPageWrapper>
      <div className='container'>
        <Title size='MD' className='organizer-name'>
          {data?.firstName} {data?.lastName}
        </Title>
        <form
          className='organizer-form-container'
          onKeyDown={handleKeyDown}
          onSubmit={handleSubmit}
        >
          <Input
            type='tel'
            name='phone'
            className='organizer-form-input'
            label={tr('phone_number')}
            value={organizerData.phone}
            onChange={handleChange}
            autoComplete='off'
            error={errors.phone}
          />
          <Input
            type='password'
            name='currentPassword'
            label={tr('current_password')}
            className='organizer-form-input'
            value={organizerData.currentPassword}
            onChange={handleChange}
            autoComplete='off'
            error={errors.currentPassword}
          />
          <Input
            type='password'
            name='newPassword'
            label={tr('new_password')}
            className='organizer-form-input'
            value={organizerData.newPassword}
            onChange={handleChange}
            autoComplete='off'
            error={errors.newPassword}
          />
          <Button className='organizer-form-button' type='submit' $block>
            {tr('save')}
          </Button>
        </form>
      </div>
    </OrganizerPageWrapper>
  );
};

export default OrganizerAccount;
